import { apiClient } from '@borradh-workspace/api-client';
import { conversationMessageSchema } from '@borradh-workspace/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { messageTransportValues, messagingPlatformValues } from '../types';

const platformSchema = z.enum(messagingPlatformValues);

const timelineSchema = z.object({
  items: z.array(
    conversationMessageSchema.extend({ platform: platformSchema })
  ),
  channels: z.array(
    z.object({
      conversationId: z.string(),
      platform: platformSchema,
      transport: z.enum(messageTransportValues).nullable().optional(),
      status: z.string(),
      canReply: z.boolean(),
      lastCustomerMessageAt: z.string().nullable(),
    })
  ),
  replyConversationId: z.string().nullable(),
});
export type CustomerTimeline = z.infer<typeof timelineSchema>;
export type TimelineChannel = CustomerTimeline['channels'][number];
export type TimelineMessage = CustomerTimeline['items'][number];

/** Cache key shape, shared with the send hook's invalidation. */
export const customerTimelineKey = (conversationId: string) =>
  ['conversations', conversationId, 'timeline'] as const;

/**
 * A conversation as the inbox shows it. With the merged inbox on, every
 * message from all of the customer's channels, each tagged with its channel,
 * plus where a reply can go; otherwise just this thread.
 */
export const customerTimelineQueryOptions = (conversationId: string) =>
  queryOptions({
    queryKey: customerTimelineKey(conversationId),
    queryFn: () =>
      apiClient.get(`conversations/${conversationId}/timeline?limit=100`, {
        schema: timelineSchema,
      }),
    enabled: !!conversationId,
    staleTime: 5 * 1000,
    refetchInterval: 3000,
  });

export const useCustomerTimeline = (conversationId: string) => {
  const query = useQuery(customerTimelineQueryOptions(conversationId));
  return {
    messages: query.data?.items ?? [],
    channels: query.data?.channels ?? [],
    replyConversationId: query.data?.replyConversationId ?? null,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
